/* eslint-disable no-console */
import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import { FormErrors, Locales } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { v4 } from "uuid";

import { MinimalLayout } from "../../../common/layouts/MinimalLayout";

import {
  sendEmbedConnectedMessage,
  sendEmbedResizedMessage,
} from "@/canvas/features/FormV2/helpers/dynamicEmbedMessaging";
import { addCacheTagHeaderToServerResponse } from "@/common/helpers/cache";
import { getSSRNewsletterForm, getTranslation, provideOrganization } from "@/common/helpers/getStaticProps";
import { DASHBOARD_TRANSLATION_FILES } from "@/common/helpers/translationFiles";
import { useElementSize } from "@/common/hooks/useElementSize";
import { usePageScrollUp } from "@/common/hooks/usePageScrollUp";
import { useTheme } from "@/common/hooks/useTheme";
import { ServerSideContext } from "@/common/types/app";
import { OrganizationOutput, NewsletterFormFrontendPageOutput } from "@/common/types/trpc";
import { AppFormProvider } from "@/components/AppProvider/AppFormProvider/AppFormProvider";
import { ZeffyHead } from "@/components/Head/ZeffyHead";
import NotFoundWithProviders from "@/components/NotFound/NotFoundWithProviders";
import { NewsletterFormContainer } from "@/features/NewsletterForm/Signup/NewsletterFormContainer";

type NewsletterFormWithOrganization = NewsletterFormFrontendPageOutput & {
  organization: OrganizationOutput;
};

type NewsletterFormPageProps =
  | {
      newsletterForm: NewsletterFormWithOrganization;
      isDeleted?: false;
    }
  | {
      newsletterForm?: null;
      isDeleted: true;
    };

const NewsletterFormPage = ({ newsletterForm, isDeleted }: NewsletterFormPageProps) => {
  usePageScrollUp();

  const theme = useTheme({ color: newsletterForm?.buttonColor, transparent: true });

  const searchParams = useSearchParams();
  const embedId = searchParams.get("embedId");
  const isEmbedV2 = embedId !== null;

  const [embedContainer, setEmbedContainer] = useState<HTMLDivElement | null>(null);
  const embedSize = useElementSize(embedContainer);
  const isFormMounted = embedContainer !== null;

  // Cross-frame post, so it cannot happen during render. Gated on mount because connecting drops the host
  // skeleton; not on a measured height, since rAF is throttled offscreen and footer embeds would never connect.
  useEffect(() => {
    if (isEmbedV2 && !isDeleted && isFormMounted) {
      sendEmbedConnectedMessage();
    }
  }, [isEmbedV2, isDeleted, isFormMounted]);

  // Same cross-frame post: the host sizes its container from this, and a height is only known after layout.
  useEffect(() => {
    if (isEmbedV2 && !isDeleted && embedSize.scrollHeight > 0) {
      sendEmbedResizedMessage(embedSize.scrollHeight);
    }
  }, [isEmbedV2, embedSize.scrollHeight, isDeleted]);

  if (isDeleted) {
    return (
      <>
        <ZeffyHead />
        <NotFoundWithProviders notFoundEntity="signUpForm" />
      </>
    );
  }

  return (
    <>
      {/* Nothing should wrap or block the Head component */}
      <ZeffyHead title={newsletterForm.title} description="" />
      <AppFormProvider
        theme={theme}
        organization={newsletterForm.organization}
        fields={[{ locale: newsletterForm.locale }]}
        themeColor={newsletterForm.buttonColor}
        isEmbed
        formData={null}
        formType={null}
        SSREnabled={false} // Newsletter has issues with translations when SSR is enabled, do not enable until issue is fixed
      >
        <MinimalLayout hideBackground>
          <Box ref={setEmbedContainer}>
            <NewsletterFormContainer newsletterForm={newsletterForm} isEmbedded />
          </Box>
        </MinimalLayout>
      </AppFormProvider>
    </>
  );
};

export const getServerSideProps = async (context: ServerSideContext<{ newsletterFormId: string }>) => {
  const requestId = (context.req?.headers?.["x-request-id"] as string) || v4();
  const { newsletterFormId } = context.params;

  const locale = context.locale.toUpperCase() as Locales;

  try {
    const [newsletterFormResponse, translations] = await Promise.all([
      getSSRNewsletterForm({ newsletterFormIdOrUrlPath: newsletterFormId, requestId }),
      getTranslation(context.locale, DASHBOARD_TRANSLATION_FILES),
    ]);

    if (newsletterFormResponse.error) {
      const code = newsletterFormResponse.error.code;

      if (code === FormErrors.FormDeleted) {
        if (context.res) {
          context.res.statusCode = 410;
        }

        console.log(`Form deleted - redirecting to gone newsletterFormId=${newsletterFormId} ssrId=${requestId}`);
        return {
          props: {
            ...translations,
            isDeleted: true,
          },
        };
      }

      console.log(
        `NEXT_ERROR: form not found - redirecting to notFound newsletterFormId=${newsletterFormId} ssrId=${requestId}`
      );
      return { props: {}, notFound: true };
    }

    const cacheTags = [newsletterFormId];
    if (newsletterFormResponse.data?.id && newsletterFormResponse.data.id !== newsletterFormId) {
      cacheTags.push(newsletterFormResponse.data.id);
    }
    addCacheTagHeaderToServerResponse(context.res, cacheTags);

    return {
      props: {
        ...translations,
        newsletterForm: newsletterFormResponse.data.organization
          ? newsletterFormResponse.data
          : provideOrganization(newsletterFormResponse.data, locale),
      },
    };
  } catch (error) {
    console.log(
      `NEXT_ERROR: request failed - redirecting to notFound newsletterFormId=${newsletterFormId} ssrId=${requestId}`,
      error
    );
    return { props: {}, notFound: true };
  }
};

export default NewsletterFormPage;
